import {Network} from "../../../network/Network";
import {ProductType} from "../../../models/product/ProductType";
import {HttpStatus} from "../../../network/status/HttpStatus";
import {INetworkComponent} from "../../../network/types/INetworkComponent";
import {Component} from "../../../sedestral/framework/interface/component/Component";
import {ICommunitySEOBacklinkOverviewsIncoming} from "../../../models/community/seo/backlink/ICommunitySEOBacklinkOverviewsIncoming";
import {ICommunitySEOBacklinksReferredDomainsIncoming} from "../../../models/community/seo/backlink/ICommunitySEOBacklinksReferredDomainsIncoming";
import {ICommunitySEOBacklinksRecentIncoming} from "../../../models/community/seo/backlink/ICommunitySEOBacklinksRecentIncoming";
import {ICommunitySEOBacklinksSearchOutgoing} from "../../../models/community/seo/backlink/ICommunitySEOBacklinksSearchOutgoing";
import {ICommunitySEOBacklinkUpdateIncoming} from "../../../models/community/seo/backlink/ICommunitySEOBacklinkUpdateIncoming";
import {WebSocketCommunityEventName} from "../../../network/socket/names/WebSocketCommunityEventName";
import {ICommunitySEOBacklinkEvaluationIncoming} from "../../../models/community/seo/backlink/evaluation/ICommunitySEOBacklinkEvaluationIncoming";
import {ICommunitySEOBacklinkEvaluationOutgoing} from "../../../models/community/seo/backlink/evaluation/ICommunitySEOBacklinkEvaluationOutgoing";
import {ICommunitySEOBacklinkEvaluationHistoryIncoming} from "../../../models/community/seo/backlink/evaluation/ICommunitySEOBacklinkEvaluationHistoryIncoming";
import {ICommunitySEOBacklinksToxicDomainsIncoming} from "../../../models/community/seo/backlink/ICommunitySEOBacklinksToxicDomainsIncoming";
import {arrayRemove} from "../../../sedestral/framework/utilities/ArrayRemove";

export class CommunityBacklinkService {

    public static events: ((update: ICommunitySEOBacklinkUpdateIncoming) => void)[] = [];
    public static evaluationEvents: ((update: { communityId: string, evaluation: ICommunitySEOBacklinkEvaluationIncoming }) => void)[] = [];
    public static overviewRefreshEvents: { component: Component, func: (communityId: string) => void }[] = [];
    private static overviewRefreshPending: boolean = false;

    public static dispose(): void {
        this.events = [];
        this.evaluationEvents = [];
        this.overviewRefreshEvents = [];
        this.overviewRefreshPending = false;
    }

    public static init(): void {
        Network.on(ProductType.COMMUNITY, WebSocketCommunityEventName.COMMUNITY_SEO_BACKLINK_UPDATE, (update: ICommunitySEOBacklinkUpdateIncoming) => {
            this.events.forEach(callback => callback(update));
            this.scheduleOverviewRefresh(update.communityId);
        });
        Network.on(ProductType.COMMUNITY, WebSocketCommunityEventName.COMMUNITY_SEO_BACKLINK_EVALUATION_UPDATE, (update: { communityId: string, evaluation: ICommunitySEOBacklinkEvaluationIncoming }) => {
            this.evaluationEvents.forEach(callback => callback(update));
        });
    }

    public static onBacklinkUpdate(component: INetworkComponent, func: (update: ICommunitySEOBacklinkUpdateIncoming) => void): void {
        this.events.push(func);
        component.onRemove(() => arrayRemove(this.events, func));
    }

    public static onEvaluationUpdate(component: INetworkComponent, func: (update: { communityId: string, evaluation: ICommunitySEOBacklinkEvaluationIncoming }) => void): void {
        this.evaluationEvents.push(func);
        component.onRemove(() => arrayRemove(this.evaluationEvents, func));
    }

    public static onOverviewRefresh(component: Component, func: (communityId: string) => void): void {
        const entry = {component, func};
        this.overviewRefreshEvents.push(entry);
        component.onRemove(() => arrayRemove(this.overviewRefreshEvents, entry));
    }

    private static scheduleOverviewRefresh(communityId: string): void {
        if (this.overviewRefreshPending || this.overviewRefreshEvents.length === 0) {
            return;
        }
        this.overviewRefreshPending = true;
        this.overviewRefreshEvents[0].component.timeOut(() => {
            this.overviewRefreshEvents.forEach(entry => entry.func(communityId));
            this.overviewRefreshPending = false;
        }, 20000);
    }

    public static async getOverviews(communityId: string, dateStart: number, dateEnd: number, component?: INetworkComponent): Promise<ICommunitySEOBacklinkOverviewsIncoming> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/backlinks/overviews/${communityId}?dateStart=${dateStart}&dateEnd=${dateEnd}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async getReferredDomains(searchOutgoing: ICommunitySEOBacklinksSearchOutgoing, component?: INetworkComponent): Promise<ICommunitySEOBacklinksReferredDomainsIncoming> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/backlinks/referred-domains`, searchOutgoing, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async getRecentBacklinks(searchOutgoing: ICommunitySEOBacklinksSearchOutgoing, component?: INetworkComponent): Promise<ICommunitySEOBacklinksRecentIncoming> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/backlinks/recent`, searchOutgoing, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async getToxicDomains(communityId: string, component?: INetworkComponent): Promise<ICommunitySEOBacklinksToxicDomainsIncoming> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/backlinks/toxic-domains/${communityId}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async exportDisavowWithDomains(communityId: string, domains: string[], component?: INetworkComponent): Promise<Blob> {
        let request = await Network.postJsonBlob(ProductType.COMMUNITY, `/community/backlinks/export/disavow`, {
            communityId: communityId,
            domains: domains
        }, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async createEvaluation(communityId: string, outgoing: ICommunitySEOBacklinkEvaluationOutgoing, component?: INetworkComponent): Promise<ICommunitySEOBacklinkEvaluationIncoming> {
        let request = await Network.postJson(ProductType.COMMUNITY, `/community/backlinks/evaluation/create/${communityId}`, outgoing, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async getEvaluationById(communityId: string, evaluationId: string, component?: INetworkComponent): Promise<ICommunitySEOBacklinkEvaluationIncoming> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/backlinks/evaluation/${communityId}/${evaluationId}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }

    public static async getEvaluationHistory(communityId: string, component?: INetworkComponent): Promise<ICommunitySEOBacklinkEvaluationHistoryIncoming[]> {
        let request = await Network.get(ProductType.COMMUNITY, `/community/backlinks/evaluations/history/${communityId}`, component);
        if (request.status == HttpStatus.OK) {
            return request.data;
        }
    }
}